import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AssistantProvider } from './contexts/AssistantContext';
import { StyleProvider } from './contexts/StyleContext';
import { supabase } from './supabaseClient';
import { theme } from './styles/theme';
import Header from './components/common/Header';
import LandingPage from './components/LandingPage';
import MaxComply from './components/MaxComply';
import AuditProgram from './components/AuditProgram';
import QACertifications from './components/QACertifications';
import QATraining from './components/QATraining';
import AuditManagement from './components/AuditManagement';
import FormsAndDocuments from './components/FormsAndDocuments';
import AuditSchedule from './components/AuditSchedule';
import RootCauseAnalysisPro from './components/RootCauseAnalysisPro';
import SMSPrime from './components/SMSPrime';
import HIRA from './components/sms-prime/HIRA';
import ReporterApp from './components/sms-prime/ReporterApp';
import './App.css';

// Protected route wrapper
const ProtectedRoute = ({ user, children }) => {
  if (!user) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Coming Soon component
const ComingSoon = ({ user, setUser, title }) => (
  <div className="min-h-screen bg-white">
    <Header user={user} setUser={setUser} title={title} />
    <div className="container mx-auto px-4 py-8 text-center">
      <p className="text-gray-600">Coming Soon</p>
    </div>
  </div>
);

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check initial auth state
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user || null);
    };
    checkUser();

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null);
    });

    return () => {
      if (authListener && authListener.unsubscribe) {
        authListener.unsubscribe();
      }
    };
  }, []);

  return (
    <Router>
      <AssistantProvider>
        <StyleProvider>
          <div style={{ backgroundColor: theme.colors.background.default }} className="min-h-screen">
            <Routes>
              {/* Landing Page */}
              <Route path="/" element={<LandingPage user={user} setUser={setUser} />} />

              {/* Max_Comply Routes */}
              <Route path="/max-comply" element={
                <ProtectedRoute user={user}>
                  <MaxComply user={user} setUser={setUser} />
                </ProtectedRoute>
              } />
              <Route path="/max-comply/audit-program" element={
                <ProtectedRoute user={user}>
                  <AuditProgram user={user} setUser={setUser} />
                </ProtectedRoute>
              } />
              <Route path="/max-comply/qa-certifications" element={
                <ProtectedRoute user={user}>
                  <QACertifications user={user} setUser={setUser} />
                </ProtectedRoute>
              } />
              <Route path="/max-comply/qa-training" element={
                <ProtectedRoute user={user}>
                  <QATraining user={user} setUser={setUser} />
                </ProtectedRoute>
              } />
              <Route path="/max-comply/audit-management" element={
                <ProtectedRoute user={user}>
                  <AuditManagement user={user} setUser={setUser} />
                </ProtectedRoute>
              } />
              <Route path="/max-comply/forms-documents" element={
                <ProtectedRoute user={user}>
                  <FormsAndDocuments user={user} setUser={setUser} />
                </ProtectedRoute>
              } />
              <Route path="/max-comply/audit-schedule" element={
                <ProtectedRoute user={user}>
                  <AuditSchedule user={user} setUser={setUser} />
                </ProtectedRoute>
              } />

              {/* Other Protected Routes */}
              <Route path="/rca-pro/*" element={
                <ProtectedRoute user={user}>
                  <RootCauseAnalysisPro user={user} setUser={setUser} />
                </ProtectedRoute>
              } />
              <Route path="/sms-prime" element={
                <ProtectedRoute user={user}>
                  <SMSPrime user={user} setUser={setUser} />
                </ProtectedRoute>
              } />
              <Route path="/sms-prime/hira" element={
                <ProtectedRoute user={user}>
                  <HIRA user={user} setUser={setUser} />
                </ProtectedRoute>
              } />
              
              {/* Reporter Routes - Dedicated interface for safety reporters */}
              <Route path="/report" element={<ReporterApp />} />
              <Route path="/report/*" element={<ReporterApp />} />
              
              <Route path="/just-culture" element={
                <ProtectedRoute user={user}>
                  <ComingSoon user={user} setUser={setUser} title="Just_Culture_Flow" />
                </ProtectedRoute>
              } />

              {/* Fallback Route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </StyleProvider>
      </AssistantProvider>
    </Router>
  );
}

export default App;
